/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import styled from '@emotion/styled/macro'
import { Tooltip } from 'antd'
import Periods from '@sportninja/common/constants/periodTypes'
import { paragraphSmall, weightSemiBold } from 'src/components/css'
import colors from '@sportninja/common/constants/appColors'

const BoxScoreContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 100%;
`

const Header = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
`

const Title = styled.div`
  ${paragraphSmall}
  ${weightSemiBold}
  color: #c1c2c3;
  text-transform: uppercase;
  line-height: 16.8px;
  letter-spacing: 1.68px;
  word-wrap: break-word;
`

const ViewAllButton = styled.div`
  opacity: 0;
  text-align: center;
  color: ${colors.PRIMARY};
  font-size: 12px;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  line-height: 17.4px;
  word-wrap: break-word;
`

const ScoreCard = styled.div`
  padding: 16px;
  background: #27303e;
  border-radius: 8px;
  border: 1px solid #323a48;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 280px;
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 16px;
`

const TeamImageCell = styled.div`
  width: 32px;
  min-width: 32px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.7px;
`

const PeriodHeaderCell = styled.div`
  flex: 1;
  min-width: 0;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #c1c2c3;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  line-height: 20.3px;
`

const Divider = styled.div`
  height: 0;
  border: 1px solid #323a48;
  align-self: stretch;
`

const TeamRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 32px;
`

const ScoreCell = styled.div`
  flex: 1;
  min-width: 0;
  height: 24px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 16px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  line-height: 23.2px;
`

const GameBoxScore = ({
  game,
  isMobile,
  isSoccer = false,
  isGenericGame = false,
}) => {
  if (!game.started) {
    return null
  }

  const remainingPeriods = isSoccer
    ? [
        { id: '2', name: '2nd' },
        { id: '3', name: 'ET1' },
        { id: '4', name: 'ET2' },
      ]
    : isGenericGame
    ? []
    : [
        { id: '2', name: '2' },
        { id: '3', name: '3' },
      ]

  // Create array of all periods (actual + remaining)
  const allPeriods = [...game.periods]
  if (game.periods.length < 3 && !isGenericGame) {
    const remaining = remainingPeriods.slice(game.periods.length)
    allPeriods.push(...remaining)
  }

  return (
    <BoxScoreContainer css={css``}>
      <Header>
        <Title>BOX SCORE</Title>
        <ViewAllButton>View All</ViewAllButton>
      </Header>

      <ScoreCard>
        {/* Header Row */}
        <HeaderRow>
          <TeamImageCell> </TeamImageCell>
          {game.periods.map((period, idx) => {
            if (isSoccer) {
              return (
                <PeriodHeaderCell key={period.id}>
                  {Periods[period?.period_type?.id]}
                </PeriodHeaderCell>
              )
            }
            const name = period?.period_type?.is_overtime ? 'OT' : idx + 1
            return <PeriodHeaderCell key={period.id}>{name}</PeriodHeaderCell>
          })}
          {game.periods.length < 3 &&
            !isGenericGame &&
            remainingPeriods
              .slice(game.periods.length)
              .map((period) => (
                <PeriodHeaderCell key={period.id}>
                  {period.name}
                </PeriodHeaderCell>
              ))}
          <PeriodHeaderCell>Total</PeriodHeaderCell>
        </HeaderRow>

        <Divider />

        {/* Home Team Row */}
        <TeamRow>
          <img
            src={
              game.homeTeam.imageId ||
              '/images/generic_placeholders/placeholder_team.png'
            }
            alt={game.homeTeam.name}
            css={css`
              width: 32px;
              height: 32px;
              object-fit: contain;
              background: transparent;
              border-radius: 8px;
            `}
            onError={(e) => {
              e.target.src = '/images/generic_placeholders/placeholder_team.png'
            }}
          />
          {game.periods.map((period) => (
            <ScoreCell key={`home-${period.id}`}>
              {period.goals_home_count || '0'}
            </ScoreCell>
          ))}
          {game.periods.length < 3 &&
            !isGenericGame &&
            remainingPeriods
              .slice(game.periods.length)
              .map((period) => (
                <ScoreCell key={`home-${period.id}`}>-</ScoreCell>
              ))}
          <ScoreCell>{game.homeTeamScore}</ScoreCell>
        </TeamRow>

        {/* Visiting Team Row */}
        <TeamRow>
          <img
            src={
              game.visitingTeam.imageId ||
              '/images/generic_placeholders/placeholder_team.png'
            }
            alt={game.visitingTeam.name}
            css={css`
              width: 32px;
              height: 32px;
              object-fit: contain;
              background: transparent;
              border-radius: 8px;
            `}
            onError={(e) => {
              e.target.src = '/images/generic_placeholders/placeholder_team.png'
            }}
          />
          {game.periods.map((period) => (
            <ScoreCell key={`visiting-${period.id}`}>
              {period.goals_visiting_count || '0'}
            </ScoreCell>
          ))}
          {game.periods.length < 3 &&
            !isGenericGame &&
            remainingPeriods
              .slice(game.periods.length)
              .map((period) => (
                <ScoreCell key={`visiting-${period.id}`}>-</ScoreCell>
              ))}
          <ScoreCell>{game.visitingTeamScore}</ScoreCell>
        </TeamRow>
      </ScoreCard>
    </BoxScoreContainer>
  )
}

export default GameBoxScore
